// src/pages/negocio/ReportesNegocio.js
//
// Reportes del negocio. Reutiliza el mismo endpoint del panel con
// rango extendido y expone tabla de distribuciones y descarga a JSON.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Grid, TextField, Alert,
  CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Divider,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell,
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';

import { panel as apiPanel } from '../../api/panelApi';
import { formatoCOP } from '../../utils/format';

const hoy       = () => new Date().toISOString().slice(0, 10);
const haceDias  = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const COLORES_ESTADO = { nuevo: '#9E9E9E', preparacion: '#0288D1', en_ruta: '#F57C00', entregado: '#388E3C', cancelado: '#C62828' };

export default function ReportesNegocio() {
  const [desde, setDesde] = useState(haceDias(30));
  const [hasta, setHasta] = useState(hoy());
  const [data, setData]   = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await apiPanel.negocio(desde, hasta);
      setData(r);
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar el reporte.'); }
    finally { setCargando(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const descargar = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `holaveci-reporte-${desde}-${hasta}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Reportes</Typography>
          <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Reportes del negocio</Typography>
        </Box>
        <Button startIcon={<DownloadIcon />} onClick={descargar} disabled={!data} variant="outlined">
          Descargar JSON
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="Desde" value={desde}
                onChange={(e) => setDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="Hasta" value={hasta}
                onChange={(e) => setHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <>
          {/* KPIs */}
          <Grid container spacing={2}>
            <KPICard label="Total vendido" valor={formatoCOP(data.totalVendido || data.TotalVendido || 0)} />
            <KPICard label="Pedidos" valor={data.cantidadPedidos ?? data.CantidadPedidos ?? 0} />
            <KPICard label="Ticket promedio" valor={formatoCOP(data.ticketPromedio || data.TicketPromedio || 0)} />
            <KPICard label="Cumplimiento" valor={`${(data.porcentajeCumplimiento || data.PorcentajeCumplimiento || 0).toFixed(1)}%`} />
          </Grid>

          {/* Pedidos por estado */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>Distribución por estado</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(data.porEstado || data.PorEstado || []).map(x => ({
                        name: x.etiqueta || x.Etiqueta,
                        codigo: x.codigo || x.Codigo,
                        pedidos: x.cantidad || x.Cantidad,
                        total: x.total || x.Total,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="pedidos">
                          {(data.porEstado || data.PorEstado || []).map((x, i) => (
                            <Cell key={i} fill={COLORES_ESTADO[x.codigo || x.Codigo] || '#666'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Pedidos</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.porEstado || data.PorEstado || []).map((x, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={x.etiqueta || x.Etiqueta}
                              sx={{ bgcolor: COLORES_ESTADO[x.codigo || x.Codigo] || '#666', color: '#fff' }}
                            />
                          </TableCell>
                          <TableCell align="right">{x.cantidad || x.Cantidad}</TableCell>
                          <TableCell align="right">{formatoCOP(x.total || x.Total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Métodos de pago */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontFamily: 'Fraunces', mb: 2 }}>Métodos de pago</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Método</TableCell>
                    <TableCell align="right">Pedidos</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.porMetodoPago || data.PorMetodoPago || []).map((x, i) => (
                    <TableRow key={i}>
                      <TableCell>{x.etiqueta || x.Etiqueta}</TableCell>
                      <TableCell align="right">{x.cantidad || x.Cantidad}</TableCell>
                      <TableCell align="right">{formatoCOP(x.total || x.Total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}

function KPICard({ label, valor }) {
  return (
    <Grid item xs={6} md={3}>
      <Card>
        <CardContent>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontFamily: 'Fraunces' }}>{valor}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}
